'use client';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const ROICalculator: React.FC = () => {
  const [orders, setOrders] = useState(120);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Calculations
  const avgTicket = 385; // USD
  const currentMissRate = 0.08; // 8% missed billing
  const timeSavedPerRO = 26; // minutes saved per RO (from 30min to 4.2s)
  const hoursRecovered = Math.round((orders * timeSavedPerRO) / 60);
  const revenueRecovered = Math.round(orders * avgTicket * currentMissRate);
  const annualRevenue = revenueRecovered * 12;
  const errorReduction = Math.round(orders * 0.19); // avg 19% error rate on manual

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="calculator" className="py-24 px-6">
      <div className="max-w-4xl mx-auto" ref={ref}>
        {/* Label */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[11px] tracking-[0.4em] uppercase text-[rgba(192,199,208,0.5)] font-medium">
            Your Numbers
          </span>
          <h2 className="heading font-black text-white mt-3" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 3rem)' }}>
            Run your shop&apos;s numbers
          </h2>
          <p className="text-[#C0C7D0] mt-3 text-base max-w-xl mx-auto">
            Drag the slider to your average monthly repair orders. Invoice calculates your projected time saved and revenue recovered — instantly.
          </p>
        </div>

        <div
          className={`glass-panel p-8 md:p-12 teal-glow-box transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Slider input */}
          <div className="mb-10">
            <div className="flex items-end justify-between mb-4">
              <label className="text-[#C0C7D0] text-sm font-medium">Monthly repair orders</label>
              <div className="mono text-teal font-bold text-3xl">{orders}</div>
            </div>
            <input
              type="range"
              min={20}
              max={600}
              step={10}
              value={orders}
              onChange={(e) => setOrders(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between mt-2 text-[11px] text-[rgba(192,199,208,0.35)] tracking-wide">
              <span>20 / mo — Solo mobile mechanic</span>
              <span>600 / mo — Multi-bay service center</span>
            </div>
          </div>

          {/* Results grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Time saved */}
            <div className="glass-panel border-[rgba(0,229,204,0.2)] p-6 text-center">
              <div className="w-10 h-10 rounded-lg bg-[rgba(0,229,204,0.1)] flex items-center justify-center mx-auto mb-3">
                <Icon name="ClockIcon" size={20} className="text-teal" />
              </div>
              <div className="mono font-bold text-teal" style={{ fontSize: '2rem' }}>
                {hoursRecovered}h
              </div>
              <div className="text-[#C0C7D0] text-[12px] mt-1">Hours saved per month</div>
              <div className="text-[rgba(192,199,208,0.4)] text-[11px] mt-1">
                ≈ {Math.round(hoursRecovered / 8)} full workdays
              </div>
            </div>

            {/* Revenue recovered */}
            <div className="glass-panel border-[rgba(0,229,204,0.3)] p-6 text-center bg-[rgba(0,229,204,0.04)]">
              <div className="w-10 h-10 rounded-lg bg-[rgba(0,229,204,0.15)] flex items-center justify-center mx-auto mb-3">
                <Icon name="CurrencyDollarIcon" size={20} className="text-teal" />
              </div>
              <div className="mono font-bold text-teal" style={{ fontSize: '2rem' }}>
                ${revenueRecovered.toLocaleString()}
              </div>
              <div className="text-[#C0C7D0] text-[12px] mt-1">Recovered this month</div>
              <div className="text-[rgba(192,199,208,0.4)] text-[11px] mt-1">
                ${annualRevenue.toLocaleString()} / year
              </div>
            </div>

            {/* Errors eliminated */}
            <div className="glass-panel border-[rgba(0,229,204,0.2)] p-6 text-center">
              <div className="w-10 h-10 rounded-lg bg-[rgba(0,229,204,0.1)] flex items-center justify-center mx-auto mb-3">
                <Icon name="ShieldCheckIcon" size={20} className="text-teal" />
              </div>
              <div className="mono font-bold text-teal" style={{ fontSize: '2rem' }}>
                {errorReduction}
              </div>
              <div className="text-[#C0C7D0] text-[12px] mt-1">Invoice errors eliminated</div>
              <div className="text-[rgba(192,199,208,0.4)] text-[11px] mt-1">
                per month, vs. manual
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <a href="#demo" className="btn-teal px-10 py-4 text-[15px] inline-block">
              See It Beat Your Current Setup →
            </a>
            <p className="text-[11px] text-[rgba(192,199,208,0.35)] mt-3 tracking-wide">
              Based on industry averages from 14,000+ shops. Your results may vary.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;